import axios, { rawAxios } from '@/lib/axios'
import { buildAdminRequestConfig } from '@/lib/organization-scoping'
import { attachNextPricingBoundary } from '@/lib/services/pricingBoundary'
import type { QueryFunctionContext } from '@tanstack/react-query'
import z from 'zod/v4'
import type {
  CollectionDetail,
  CollectionIdInput,
  CollectionIndexFilters,
  CollectionIndexResponse,
  CollectionIndexResult,
  CollectionMediaListInput,
  CollectionResult,
  CreateCollectionInput,
  CreatedCollection,
  CollectionWithMedia,
  CreateFromGlobalCollectionInput,
  GlobalCollection,
  GlobalCollectionFilters,
  UpdateCollectionInput,
} from './collections.types'

export const collectionImportAttemptSchema = z.object({
  id: z.uuid(),
  type: z.enum(['global_collection_selection', 'global_collection_source']),
  status: z.enum(['queued', 'running', 'completed', 'failed']),
  stage: z.enum([
    'queued',
    'importing_collections',
    'syncing_products',
    'copying_photos',
    'updating_smart_collections',
    'updating_storefront',
    'finalizing',
    'completed',
    'failed',
  ]),
  stage_label: z.string(),
  message: z.string().nullable(),
  source_system: z.string().nullable(),
  progress: z.object({
    processed: z.number().int().nonnegative(),
    total: z.number().int().nonnegative(),
    stage_processed: z.number().int().nonnegative().optional(),
    stage_total: z.number().int().nonnegative().optional(),
    percent: z.number().int().min(0).max(100),
  }),
  result: z
    .object({
      summary: z.object({
        collections_imported: z.number().int().nonnegative(),
        products_imported: z.number().int().nonnegative(),
      }),
    })
    .nullable(),
  error: z
    .object({
      message: z.string(),
    })
    .nullable(),
  queued_at: z.string().nullable(),
  started_at: z.string().nullable(),
  finished_at: z.string().nullable(),
})

const collectionImportActiveAttemptResponseSchema = z.object({
  attempt: collectionImportAttemptSchema.nullable(),
})

/** Every collection request is scoped to the organization. */
export interface CollectionOrganizationScope {
  organizationId: number | string
}

/** Calls that act on one collection; their Laravel routes require its id. */
export interface CollectionScope extends CollectionOrganizationScope {
  collectionId: CollectionIdInput
}

/** Calls that read one import attempt. */
export interface CollectionImportAttemptScope extends CollectionOrganizationScope {
  importAttemptId: string
}

/** Query key entries; the scoped key builders always set the organization as a number. */
interface CollectionKeyEntries {
  organizationId: number
}

export interface CollectionIndexQueryKeyEntries extends CollectionKeyEntries {
  filters?: CollectionIndexFilters | undefined
}

export interface GlobalCollectionQueryKeyEntries extends CollectionKeyEntries {
  filters?: GlobalCollectionFilters | undefined
}

/** `collectionQueryKeys.show` always sets the id the request URL names. */
export interface CollectionShowQueryKeyEntries extends CollectionKeyEntries {
  collectionId: CollectionIdInput
}

/** Query functions read only the key. */
type CollectionQueryContext<TKey> = Pick<QueryFunctionContext<[TKey]>, 'queryKey'>

const NEXT_PRICING_BOUNDARY_HEADER = 'x-next-price-adjustment-change-at'

export async function getCollections({
  queryKey: [{ organizationId, filters = {} }],
}: CollectionQueryContext<CollectionIndexQueryKeyEntries>): Promise<CollectionIndexResult> {
  const response = await rawAxios.get<CollectionIndexResponse>(
    '/products/collections',
    buildAdminRequestConfig({ organizationId, params: { ...filters } })
  )
  const nextPriceAdjustmentChangeAt: unknown = response.headers[NEXT_PRICING_BOUNDARY_HEADER]

  return Array.isArray(response.data)
    ? attachNextPricingBoundary(response.data, nextPriceAdjustmentChangeAt, { shape: 'array' })
    : attachNextPricingBoundary(response.data, nextPriceAdjustmentChangeAt)
}

export async function getCollection({
  queryKey: [{ organizationId, collectionId }],
}: CollectionQueryContext<CollectionShowQueryKeyEntries>): Promise<CollectionResult> {
  const response = await rawAxios.get<CollectionDetail>(
    `/products/collections/${collectionId}`,
    buildAdminRequestConfig({ organizationId })
  )
  const nextPriceAdjustmentChangeAt: unknown = response.headers[NEXT_PRICING_BOUNDARY_HEADER]

  return attachNextPricingBoundary(response.data, nextPriceAdjustmentChangeAt)
}

export async function createCollection({
  organizationId,
  ...values
}: CreateCollectionInput & CollectionOrganizationScope) {
  return axios.post<CreatedCollection>(
    '/products/collections',
    values,
    buildAdminRequestConfig({ organizationId })
  )
}

/** 204 No Content: resolves to the empty body. */
export async function updateCollection({
  organizationId,
  collectionId,
  ...values
}: UpdateCollectionInput & CollectionScope) {
  return axios.put<string>(
    `/products/collections/${collectionId}`,
    values,
    buildAdminRequestConfig({ organizationId })
  )
}

/** Resolves to the collection with its image. */
export async function updateCollectionMedia({
  organizationId,
  collectionId,
  media = [],
}: CollectionScope & { media?: CollectionMediaListInput }) {
  return axios.put<CollectionWithMedia>(
    `/products/collections/${collectionId}/media`,
    { media },
    buildAdminRequestConfig({ organizationId })
  )
}

/** 204 No Content: resolves to the empty body. */
export async function deleteCollection({ organizationId, collectionId }: CollectionScope) {
  return axios.delete<string>(
    `/products/collections/${collectionId}`,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function getGlobalCollections({
  queryKey: [{ organizationId, filters = {} }],
}: CollectionQueryContext<GlobalCollectionQueryKeyEntries>) {
  return (
    (await axios.get<GlobalCollection[]>(
      '/global-collections',
      buildAdminRequestConfig({ organizationId, params: { ...filters } })
    )) || []
  )
}

export async function createFromGlobalCollection({
  organizationId,
  ...values
}: CreateFromGlobalCollectionInput & CollectionOrganizationScope) {
  const response = await axios.post<unknown>(
    '/global-collections',
    values,
    buildAdminRequestConfig({ organizationId })
  )

  return collectionImportAttemptSchema.parse(response)
}

export async function importFromGlobalCollectionSource({
  organizationId,
  sourceSystem,
}: CollectionOrganizationScope & { sourceSystem: string }) {
  const response = await axios.post<unknown>(
    `/global-collections/import-source/${sourceSystem}`,
    {},
    buildAdminRequestConfig({ organizationId })
  )

  return collectionImportAttemptSchema.parse(response)
}

export async function getGlobalCollectionImportAttempt({
  organizationId,
  importAttemptId,
}: CollectionImportAttemptScope) {
  const response = await axios.get<unknown>(
    `/global-collections/import-attempts/${importAttemptId}`,
    buildAdminRequestConfig({ organizationId })
  )

  return collectionImportAttemptSchema.parse(response)
}

export async function getActiveGlobalCollectionImportAttempt({
  organizationId,
}: CollectionOrganizationScope) {
  const response = await axios.get<unknown>(
    '/global-collections/import-attempts/active',
    buildAdminRequestConfig({ organizationId })
  )

  return collectionImportActiveAttemptResponseSchema.parse(response).attempt
}
